import {
  ChatAppRequest,
  SummarizeChatRequest,
  Config,
  GenericRagStateData,
  ResponseContext,
  ProgressCallback,
  FileUpdate,
  DocumentAPIResponse,
  FileProgressUpdate,
  ConversationToPromptRequest,
} from "./models";

import { useLogin } from "../utils/authConfig";

const getBackendUri = (): string => {
  return import.meta.env.VITE_BACKEND_URI || `${window.location.origin}/`;
};

const BACKEND_URI = getBackendUri();

export async function getHeaders(
  idToken: string | undefined,
): Promise<Record<string, string>> {
  if (useLogin) {
    if (idToken) {
      return { Authorization: `Bearer ${idToken}` };
    }
  }

  return {};
}

/**
 * "<status> <detail>" for a failed response, e.g. "400 Unsupported model. ...".
 * FastAPI sends errors as {"detail": "..."}; other bodies are used as they are.
 */
export async function errorMessageFromResponse(response: Response): Promise<string> {
  const text = await response.text();
  let detail = text;
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed.detail === "string") detail = parsed.detail;
  } catch {
    // not JSON
  }
  return `${response.status} ${detail}`.trim();
}

export async function configApi(idToken: string): Promise<Config> {
  const headers = await getHeaders(idToken);
  const response = await fetch(`${BACKEND_URI}/api/config/info`, {
    method: "GET",
    headers: { ...headers },
  });

  return (await response.json()) as Config;
}

export async function chatApi(
  useCase: string,
  request: ChatAppRequest,
  idToken: string | undefined,
  signal: AbortSignal,
): Promise<Response> {
  const headers = await getHeaders(idToken);
  return await fetch(
    `${BACKEND_URI}/api/chat/generate?use_case=${encodeURIComponent(useCase)}`,
    {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify(request),
      signal: signal,
    },
  );
}

export async function summarizeChatApi(
  useCase: string,
  request: SummarizeChatRequest,
  idToken: string | undefined,
): Promise<Response> {
  const headers = await getHeaders(idToken);
  return await fetch(
    `${BACKEND_URI}/api/chat/summary?use_case=${encodeURIComponent(useCase)}`,
    {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify(request),
    },
  );
}

export async function generatePromptApi(
  request: FormData,
  idToken: string | undefined,
): Promise<Response> {
  const headers = await getHeaders(idToken);
  // Note: Do not set Content-Type for FormData; the browser sets it with boundaries automatically
  return await fetch(
    `${BACKEND_URI}/api/prompt-generation/prompt-from-input-output`,
    {
      method: "POST",
      headers: { ...headers },
      body: request,
    },
  );
}

export async function createPromptFromConversationApi(
  request: ConversationToPromptRequest,
  idToken: string | undefined,
): Promise<Response> {
  const headers = await getHeaders(idToken);
  return await fetch(
    `${BACKEND_URI}/api/prompt-generation/prompt-from-conversation`,
    {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify(request),
    },
  );
}

// API calls to

export async function uploadFileApi(
  useCase: string,
  request: FormData,
  idToken: string | undefined,
  sessionState: string | undefined,
  onProgress?: ProgressCallback,
): Promise<FileProgressUpdate> {
  let url = `${BACKEND_URI}/api/documents/upload?use_case=${encodeURIComponent(useCase)}`;
  if (sessionState) {
    url += `&session_state=${encodeURIComponent(sessionState)}`;
  }
  const headers = await getHeaders(idToken);
  const response = await fetch(url, {
    method: "POST",
    headers: { ...headers },
    body: request,
  });

  if (!response.ok)
    throw new Error(`Uploading file failed: ${response.statusText}`);

  return await streamJsonResponse(response, onProgress);
}

export async function deleteUploadedFileApi(
  useCase: string,
  storageUrl?: string,
  filename?: string,
  idToken?: string,
  sessionState?: string,
  onProgress?: ProgressCallback,
): Promise<FileProgressUpdate> {
  const headers = await getHeaders(idToken);

  // Construct body dynamically to include only the values you have
  const body: Record<string, string | null> = {
    filename: filename ?? null,
    storage_url: storageUrl ?? null,
    session_state: sessionState ?? null,
  };

  const response = await fetch(
    `${BACKEND_URI}/api/documents/delete_single?use_case=${encodeURIComponent(useCase)}`,
    {
      method: "DELETE",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    throw new Error(`Deleting file failed: ${response.statusText}`);
  }

  // Optional: handle streaming JSON progress
  return await streamJsonResponse(response, onProgress);
}

export async function deleteAllUploadedFilesApi(
  useCase: string,
  idToken: string | undefined,
  sessionState: string | undefined,
): Promise<DocumentAPIResponse> {
  const headers = await getHeaders(idToken);
  const response = await fetch(
    `${BACKEND_URI}/api/documents/delete_all?use_case=${encodeURIComponent(useCase)}`,
    {
      method: "DELETE",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({
        session_state: sessionState,
      }),
    },
  );

  if (!response.ok)
    throw new Error(`Deleting file failed: ${response.statusText}`);

  const deleteResponse: { message: string } = await response.json();

  return {
    message: deleteResponse.message,
  };
}

export async function streamJsonResponse(
  response: Response,
  onProgress?: ProgressCallback,
): Promise<any> {
  const reader = response.body?.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let lastUpdate: any = null;

  if (!reader) throw new Error("No response body to read from");

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    // Split on newlines, keep last incomplete line in buffer
    let lines = buffer.split("\n");
    buffer = lines.pop() || "";

    for (const line of lines) {
      if (!line.trim()) continue;
      try {
        const parsed = JSON.parse(line);
        lastUpdate = parsed;
        onProgress?.(parsed);
      } catch (e) {
        console.warn("Failed to parse line:", line, e);
      }
    }
  }

  // Try parsing leftover buffer (final chunk)
  if (buffer.trim()) {
    try {
      const parsed = JSON.parse(buffer);
      lastUpdate = parsed;
      onProgress?.(parsed);
    } catch (e) {
      console.warn("Failed to parse final buffer:", buffer, e);
    }
  }

  return lastUpdate;
}

export async function listUploadedFilesApi(
  useCase: string,
  idToken: string | undefined,
  sessionState: string | undefined,
): Promise<string[]> {
  const response = await fetch(
    `${BACKEND_URI}/api/documents/list?use_case=${encodeURIComponent(useCase)}&session_state=${sessionState}`,
    {
      method: "GET",
      headers: await getHeaders(idToken),
    },
  );

  if (!response.ok) {
    throw new Error(`Listing files failed: ${response.statusText}`);
  }

  const dataResponse: { filenames: string[] } = await response.json();
  return dataResponse.filenames;
}

export async function listCommonCorpusFilesApi(
  useCase: string,
  idToken: string | undefined,
): Promise<string[]> {
  const response = await fetch(
    `${BACKEND_URI}/api/documents/list_common?use_case=${encodeURIComponent(useCase)}`,
    {
      method: "GET",
      headers: await getHeaders(idToken),
    },
  );

  if (!response.ok) {
    throw new Error(`Listing files failed: ${response.statusText}`);
  }

  const dataResponse: { filenames: string[] } = await response.json();
  return dataResponse.filenames;
}

export function getCitationFilePath(useCase: string, citation: string): string {
  const hashIndex = citation.indexOf("#");
  const filename = hashIndex !== -1 ? citation.slice(0, hashIndex) : citation;
  const hash = hashIndex !== -1 ? citation.slice(hashIndex) : "";

  return `${BACKEND_URI}/api/documents/download?filename=${encodeURIComponent(
    filename,
  )}&use_case=${encodeURIComponent(useCase)}&hash=${encodeURIComponent(hash)}`;
}

export async function logUserFeedback(
  feedback_tags: string,
  feedback_text: string,
  answer: string,
  context: ResponseContext,
  idToken: string,
): Promise<Response> {
  const headers = await getHeaders(idToken);
  const response = await fetch(`${BACKEND_URI}/api/feedback/log`, {
    method: "POST",
    body: JSON.stringify({
      feedback_tags: feedback_tags,
      feedback_text: feedback_text,
      answer: answer,
      context: context,
    }),
    headers: { ...headers, "Content-Type": "application/json" },
  })
    .then((res) => {
      return res;
    })
    .catch((err) => {
      console.error("There was an issue logging feedback.");
      let errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500,
      };
      return errRes;
    });
  return response;
}
